<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DoubleMaterialityIroImpactType,
  DoubleMaterialityIroRiskAndOpportunityType,
} from '@/__generated__/types';
import useFormatNumber from '@/utils/composables/useFormatNumber';
import { useStore } from '../../store';
import OgTable from '../../OgTable.vue';
import {getSurveysData, surveyHeaders} from '@/components/pages/Modules/DoubleMateriality/PgResults/PgList/dataGenerators';
import type { OgSurveysRowItem } from '@/components/pages/Modules/DoubleMateriality/PgResults/PgList/types';

const { t } = useI18n();
const store = useStore();
const { formatNumber } = useFormatNumber();

const headers = computed(() =>
  surveyHeaders.map((h) => ({ ...h, text: t(h.text) })),
);

const items = computed<OgSurveysRowItem[]>(() => getSurveysData(store.value));
</script>

<template>
  <h3 class="text-lg font-medium mb-3">
    {{ t('Stakeholder survey results') }}
  </h3>
  <OgTable :headers="headers" :items="items" :isRowRemovable="false">
    <template #item-esrs="row: OgSurveysRowItem">
      <p>{{ row.topic.esrs }} {{ t(row.topic.topic) }}</p>
    </template>
    <template #item-sustainabilityMatter="row: OgSurveysRowItem">
      <p>
        {{ t(row.topic.subSubTopic) || t(row.topic.subTopic) || '' }}
      </p>
    </template>
    <template #item-iro="row: OgSurveysRowItem">
      <p>
        {{
          row.iroImpact?.impact ??
          row.iroRiskAndOpportunity?.riskAndOpportunity ??
          ''
        }}
      </p>
    </template>
    <template #item-iroType="row: OgSurveysRowItem">
      <p>
        <template
          v-if="
            row.iroImpact?.type ===
            DoubleMaterialityIroImpactType.ACTUAL_NEGATIVE
          "
        >
          {{ t('Actual negative impact') }}
        </template>
        <template
          v-else-if="
            row.iroImpact?.type ===
            DoubleMaterialityIroImpactType.POTENTIAL_NEGATIVE
          "
        >
          {{ t('Potential negative impact') }}
        </template>
        <template
          v-else-if="
            row.iroImpact?.type ===
            DoubleMaterialityIroImpactType.ACTUAL_POSITIVE
          "
        >
          {{ t('Actual positive impact') }}
        </template>
        <template
          v-else-if="
            row.iroImpact?.type ===
            DoubleMaterialityIroImpactType.POTENTIAL_POSITIVE
          "
        >
          {{ t('Potential positive impact') }}
        </template>
        <template
          v-else-if="
            row.iroRiskAndOpportunity?.type ===
            DoubleMaterialityIroRiskAndOpportunityType.OPPORTUNITY
          "
        >
          {{ t('Opportunity') }}
        </template>
        <template
          v-else-if="
            row.iroRiskAndOpportunity?.type ===
            DoubleMaterialityIroRiskAndOpportunityType.RISK
          "
        >
          {{ t('Risk') }}
        </template>
        <template v-else>
          {{ t('n/a') }}
        </template>
      </p>
    </template>
    <template #item-totalScore="row: OgSurveysRowItem">
      <p>
        {{
          typeof row.totalScore !== 'number'
            ? t('n/a')
            : formatNumber(row.totalScore, 0, 2)
        }}
      </p>
    </template>
  </OgTable>
</template>
